/**
 * i18n für die VeriFactu-Seite (`/settings/verifactu`).
 *
 * Diese App lädt `plugin-invoice` nicht — es gibt hier also weder den
 * `invoice`-Namespace noch eine Trägerseite für die ES-Kachel. Statt das ganze
 * Invoice-Plugin nachzurüsten, liegen hier genau die Keys, die
 * `VerifactuSettingsPage` (invoice-frontend) anfragt, unter einem eigenen
 * Namespace `verifactu`. Die Keys behalten ihr `invoice.`-Präfix, weil die
 * Komponente sie so nachschlägt.
 *
 * Wortlaut aus `@xhubio-saas/invoice-frontend` (locales/de|en) übernommen —
 * der spanische Rechtstext (declaración responsable) kommt vom Backend und
 * wird NICHT übersetzt.
 */

export const verifactuLocalesDe = {
  'invoice.verifactu.page.title': 'VeriFactu',
  'invoice.verifactu.page.description':
    'Pflichtangaben für dein spanisches Rechnungssystem: Herstellererklärung und Meldestatus an die AEAT.',
  'invoice.verifactu.notSpanish':
    'VeriFactu gilt nur für spanische Betriebe. Stelle das Land deiner Organisation auf Spanien um, wenn du hier arbeiten möchtest.',

  // Aktivierung pro Betrieb (Art. 2.c Orden) inkl. Jahresbindung (Art. 16.5 RRSIF)
  'invoice.verifactu.activation.title': 'VeriFactu aktivieren',
  'invoice.verifactu.activation.hint':
    'VeriFactu wird pro Betrieb aktiviert. Ist es an, bekommt jede spanische Rechnung den AEAT-QR-Code und wird automatisch als Registro an die AEAT gemeldet.',
  'invoice.verifactu.activation.binding':
    'Achtung, das bindet dich rechtlich: Wer VeriFactu startet, bleibt mindestens bis zum 31.12. des laufenden Jahres dabei (Art. 16.5 RRSIF). Vorher kannst du nicht zurück.',
  'invoice.verifactu.activation.stateOn': 'VeriFactu ist aktiv',
  'invoice.verifactu.activation.stateOff': 'VeriFactu ist nicht aktiv',
  'invoice.verifactu.activation.boundUntil': 'Gebunden bis',
  'invoice.verifactu.activation.lockedHint':
    'Abschalten ist erst nach Ablauf der Bindung wieder möglich.',
  'invoice.verifactu.activation.confirm.title': 'VeriFactu jetzt aktivieren?',
  'invoice.verifactu.activation.confirm.description':
    'Ab sofort meldet die App jede spanische Rechnung an die AEAT und druckt den QR-Code aufs Dokument. Du bindest dich damit bis zum 31.12. des laufenden Jahres.',
  'invoice.verifactu.activation.confirm.cancel': 'Abbrechen',
  'invoice.verifactu.activation.confirm.enable': 'Verbindlich aktivieren',
  'invoice.verifactu.activation.enabled': 'VeriFactu ist aktiviert',
  'invoice.verifactu.activation.disabled': 'VeriFactu ist deaktiviert',
  'invoice.verifactu.activation.saveError': 'Aktivierung konnte nicht gespeichert werden.',

  // Declaración responsable (Art. 13 RD 1007/2023, Art. 15 Orden)
  'invoice.verifactu.declaracion.title': 'Declaración responsable',
  'invoice.verifactu.declaracion.hint':
    'Gesetzliche Herstellererklärung zu dieser Software-Version (Art. 13 RD 1007/2023). Sie muss in der Software einsehbar sein — der Wortlaut ist spanisch vorgeschrieben.',
  'invoice.verifactu.declaracion.download': 'Als Textdatei herunterladen',
  'invoice.verifactu.declaracion.version': 'Version',
  'invoice.verifactu.declaracion.legalReview':
    'Achtung: Dieser Text ist noch nicht juristisch freigegeben. Gib ihn erst an Kunden oder die AEAT weiter, wenn der Hinweis „LEGAL REVIEW REQUIRED" entfernt ist.',
  'invoice.verifactu.declaracion.loadError':
    'Die Herstellererklärung kann nicht erzeugt werden — dem Betreiber fehlt Konfiguration:',

  // Offene Meldungen (Art. 16.4 Orden)
  'invoice.verifactu.queue.title': 'Offene Meldungen',
  'invoice.verifactu.queue.hint':
    'Registros, die die AEAT nicht angenommen hat. Wiederholungen laufen automatisch; hier kannst du sie zusätzlich manuell anstoßen.',
  'invoice.verifactu.pending.label': 'Registros warten noch auf die Übermittlung an die AEAT.',
  'invoice.verifactu.retry.success': 'Erneute Übermittlung eingeplant',
  'invoice.verifactu.retry.error': 'Erneute Übermittlung fehlgeschlagen',

  // Queue-Tabelle (VerifactuStatus)
  'invoice.verifactu.title': 'Verifactu-Status',
  'invoice.verifactu.status.PENDING': 'Wartend',
  'invoice.verifactu.status.PROCESSING': 'Wird übermittelt',
  'invoice.verifactu.status.ACCEPTED': 'Angenommen (AEAT)',
  'invoice.verifactu.status.REJECTED': 'Abgelehnt (Wiederholung geplant)',
  'invoice.verifactu.status.GIVEN_UP': 'Aufgegeben — manuelle Bearbeitung nötig',
  'invoice.verifactu.recordType.ALTA': 'Ausstellung',
  'invoice.verifactu.recordType.ANULACION': 'Stornierung',
  'invoice.verifactu.attempts': 'Versuche',
  'invoice.verifactu.csv': 'AEAT-CSV',
  'invoice.verifactu.lastError': 'Letzter Fehler',
  'invoice.verifactu.nextRetryAt': 'Nächster Versuch',
  'invoice.verifactu.retry': 'Jetzt erneut versuchen',
  'invoice.verifactu.empty': 'Nicht an Verifactu übermittelt.',

  // Zustand der Kette + Herausgabe der Registros (Art. 14 RD 1007/2023).
  // 🔴 Wortlaut aus invoice-frontend uebernommen — der Waechter
  // tests/locales/verifactuSchluesselDeckung.test.ts haelt die Abschrift vollstaendig.
  'invoice.verifactu.chainExport.action': 'Kette exportieren',
  'invoice.verifactu.chainExport.hint':
    'Gibt alle Registros dieser Kette als JSON-Datei heraus — mit dem eingefrorenen Registro und der Huella jedes Eintrags, sodass sie nachgerechnet werden kann (Art. 14 RD 1007/2023).',
  'invoice.verifactu.chainExport.success': 'Kette heruntergeladen',
  'invoice.verifactu.chainExport.error': 'Die Kette konnte nicht exportiert werden.',
  'invoice.verifactu.chainExport.from': 'Von',
  'invoice.verifactu.chainExport.to': 'Bis',
  'invoice.verifactu.chainExport.rangeInvalid': 'Das Von-Datum liegt nach dem Bis-Datum.',

  // Abgleich mit der AEAT (ConsultaFactuSistemaFacturacion) — Plan API 09-03/04
  'invoice.verifactu.abgleich.title': 'Abgleich mit der AEAT',
  'invoice.verifactu.abgleich.hint':
    'Fragt bei der AEAT ab, welche Registros sie für diesen Monat gespeichert hat, und vergleicht sie mit dieser Kette. Der wichtige Fall ist die Gegenrichtung: ein Beleg, den wir gesendet haben und der dort fehlt.',
  'invoice.verifactu.abgleich.action': 'Abgleich starten',
  'invoice.verifactu.abgleich.running': 'Abgleich läuft …',
  'invoice.verifactu.abgleich.period.year': 'Jahr',
  'invoice.verifactu.abgleich.period.month': 'Monat',
  'invoice.verifactu.abgleich.nifMissing':
    'Für die Abfrage fehlt der NIF oder der Firmenname der Organisation. Beides steht im Organisationsprofil (Steuernummer und Firmenname) — ohne sie weist die AEAT die Abfrage ab.',
  'invoice.verifactu.abgleich.requestFailed': 'Die Abfrage konnte nicht ausgeführt werden.',
  'invoice.verifactu.abgleich.idle':
    'Noch nicht abgeglichen. Die Abfrage kostet einen echten Aufruf bei der Behörde und läuft deshalb nur auf Knopfdruck.',
  'invoice.verifactu.abgleich.notWired':
    'Für diese Installation ist keine Verbindung zur AEAT eingerichtet. Ein Abgleich ist erst möglich, wenn der Betreiber sie konfiguriert hat.',
  'invoice.verifactu.abgleich.blocker':
    'Die Abfrage wurde nicht gesendet, weil eine Voraussetzung fehlt. Das ist behebbar — und kein Ausfall.',
  'invoice.verifactu.abgleich.error': 'Die AEAT hat die Abfrage abgelehnt.',
  'invoice.verifactu.abgleich.incomplete':
    'Achtung: Die AEAT hat eine weitere Seite angekündigt, aber keinen Schlüssel dafür geliefert. Die Liste unten ist unvollständig — die fehlenden Belege sind NICHT geprüft.',
  'invoice.verifactu.abgleich.emptyPeriod':
    'Für diesen Monat liegen weder bei der AEAT noch in dieser Kette Registros.',
  'invoice.verifactu.abgleich.allMatching': 'Alle verglichenen Belege sind deckungsgleich.',
  'invoice.verifactu.abgleich.matching': 'Deckungsgleich',
  'invoice.verifactu.abgleich.pages': 'Abgefragte Seiten',
  'invoice.verifactu.abgleich.art.fehltBeiDerAeat': 'Fehlt bei der AEAT',
  'invoice.verifactu.abgleich.art.zustandWeichtAb': 'Zustand weicht ab',
  'invoice.verifactu.abgleich.art.fehltBeiUns': 'Fehlt in dieser Kette',
  'invoice.verifactu.abgleich.art.deckungsgleich': 'Deckungsgleich',
  'invoice.verifactu.abgleich.column.invoiceNumber': 'Rechnungsnummer',
  'invoice.verifactu.abgleich.column.sequence': 'Position in der Kette',
  'invoice.verifactu.abgleich.column.estado': 'Zustand bei der AEAT',
  'invoice.verifactu.abgleich.column.error': 'Fehler der AEAT',
  'invoice.verifactu.chainHealth.title': 'Zustand der Rechnungskette',
  'invoice.verifactu.chainHealth.hint':
    'Ergebnis der letzten nächtlichen Prüfung. Die Kette verbindet jede Rechnung mit ihrer Vorgängerin — bricht sie, ist die Unveränderbarkeit nach RD 1007/2023 nicht mehr belegbar.',
  'invoice.verifactu.chainHealth.neverRun':
    'Es liegt noch kein Prüflauf vor. Das heißt nicht, dass die Kette in Ordnung ist — es heißt, dass sie noch nicht geprüft wurde.',
  'invoice.verifactu.chainHealth.scope.invoice': 'Rechnungskette',
  'invoice.verifactu.chainHealth.scope.event': 'Ereigniskette (Registro de Eventos)',
  'invoice.verifactu.chainHealth.records': 'Geprüfte Einträge',
  'invoice.verifactu.chainHealth.lastRun': 'Letzter Prüflauf',
  'invoice.verifactu.chainHealth.intact': 'Kette in Ordnung',
  'invoice.verifactu.chainHealth.broken': 'Kette gebrochen',
  'invoice.verifactu.chainHealth.truncated':
    'Weitere Verstöße vorhanden — die Liste ist gekürzt. Der erste Eintrag oben ist die Stelle, an der es gerissen ist.',
  'invoice.verifactu.chainHealth.column.sequence': 'Nr.',
  'invoice.verifactu.chainHealth.column.kind': 'Art des Bruchs',
  'invoice.verifactu.chainHealth.column.expected': 'Erwartet',
  'invoice.verifactu.chainHealth.column.actual': 'Vorgefunden',
  'invoice.verifactu.chainHealth.kind.previous_hash_mismatch':
    'Verweis auf die Vorgängerin stimmt nicht',
  'invoice.verifactu.chainHealth.kind.sequence_gap': 'Lücke in der Nummernfolge',
  'invoice.verifactu.chainHealth.kind.genesis_mismatch': 'Kettenanfang stimmt nicht',
  'invoice.verifactu.chainHealth.kind.seed_mismatch': 'Übernommener Kettenanfang stimmt nicht',

  // Hinweis, wenn VeriFactu fuer den Betrieb nicht eingeschaltet ist.
  'invoice.verifactu.inactive.title': 'VeriFactu ist für diesen Betrieb nicht eingeschaltet',
  'invoice.verifactu.inactive.description':
    'Rechnungen, die du jetzt finalisierst, werden der AEAT nicht gemeldet — es entsteht kein Registro und das PDF bekommt keinen QR-Code. Pflicht wird die Meldung am 01.01.2027 (körperschaftsteuerpflichtige Betriebe) bzw. am 01.07.2027 (alle übrigen).',
  'invoice.verifactu.inactive.link': 'VeriFactu jetzt einschalten',
  // Sidebar
  'verifactu.nav.title': 'VeriFactu',
}

export const verifactuLocalesEn = {
  'invoice.verifactu.page.title': 'VeriFactu',
  'invoice.verifactu.page.description':
    'Mandatory information for your spanish invoicing system: manufacturer declaration and AEAT reporting status.',
  'invoice.verifactu.notSpanish':
    'VeriFactu only applies to spanish businesses. Switch your organisation country to Spain if you want to work here.',

  'invoice.verifactu.activation.title': 'Activate VeriFactu',
  'invoice.verifactu.activation.hint':
    'VeriFactu is activated per organisation. Once on, every spanish invoice carries the AEAT QR code and is reported to the AEAT as a registro automatically.',
  'invoice.verifactu.activation.binding':
    'Careful, this is legally binding: whoever starts VeriFactu stays with it at least until 31 December of the current year (art. 16.5 RRSIF). There is no way back before that.',
  'invoice.verifactu.activation.stateOn': 'VeriFactu is active',
  'invoice.verifactu.activation.stateOff': 'VeriFactu is not active',
  'invoice.verifactu.activation.boundUntil': 'Bound until',
  'invoice.verifactu.activation.lockedHint':
    'Switching off is only possible again once the binding period has ended.',
  'invoice.verifactu.activation.confirm.title': 'Activate VeriFactu now?',
  'invoice.verifactu.activation.confirm.description':
    'From now on the app reports every spanish invoice to the AEAT and prints the QR code on the document. This binds you until 31 December of the current year.',
  'invoice.verifactu.activation.confirm.cancel': 'Cancel',
  'invoice.verifactu.activation.confirm.enable': 'Activate bindingly',
  'invoice.verifactu.activation.enabled': 'VeriFactu is activated',
  'invoice.verifactu.activation.disabled': 'VeriFactu is deactivated',
  'invoice.verifactu.activation.saveError': 'Activation could not be saved.',

  'invoice.verifactu.declaracion.title': 'Declaración responsable',
  'invoice.verifactu.declaracion.hint':
    'Legal manufacturer declaration for this software version (art. 13 RD 1007/2023). It has to be viewable inside the software — the wording is prescribed in spanish.',
  'invoice.verifactu.declaracion.download': 'Download as text file',
  'invoice.verifactu.declaracion.version': 'Version',
  'invoice.verifactu.declaracion.legalReview':
    'Careful: this text has not been signed off legally yet. Only hand it to customers or the AEAT once the "LEGAL REVIEW REQUIRED" notice is gone.',
  'invoice.verifactu.declaracion.loadError':
    'The manufacturer declaration cannot be produced — the operator is missing configuration:',

  'invoice.verifactu.queue.title': 'Open submissions',
  'invoice.verifactu.queue.hint':
    'Registros the AEAT did not accept. Retries run automatically; here you can additionally trigger them manually.',
  'invoice.verifactu.pending.label': 'registros are still waiting to be sent to the AEAT.',
  'invoice.verifactu.retry.success': 'Retry scheduled',
  'invoice.verifactu.retry.error': 'Retry failed',

  'invoice.verifactu.title': 'Verifactu status',
  'invoice.verifactu.status.PENDING': 'Pending',
  'invoice.verifactu.status.PROCESSING': 'Submitting',
  'invoice.verifactu.status.ACCEPTED': 'Accepted (AEAT)',
  'invoice.verifactu.status.REJECTED': 'Rejected (retry scheduled)',
  'invoice.verifactu.status.GIVEN_UP': 'Given up — manual handling required',
  'invoice.verifactu.recordType.ALTA': 'Issuance',
  'invoice.verifactu.recordType.ANULACION': 'Cancellation',
  'invoice.verifactu.attempts': 'Attempts',
  'invoice.verifactu.csv': 'AEAT CSV',
  'invoice.verifactu.lastError': 'Last error',
  'invoice.verifactu.nextRetryAt': 'Next attempt',
  'invoice.verifactu.retry': 'Retry now',
  'invoice.verifactu.empty': 'Not submitted to Verifactu.',

  // Chain health + handing out the registros (art. 14 RD 1007/2023).
  'invoice.verifactu.chainExport.action': 'Export chain',
  'invoice.verifactu.chainExport.hint':
    'Downloads every registro of this chain as a JSON file — including the frozen registro and the huella of each entry, so it can be recomputed (art. 14 RD 1007/2023).',
  'invoice.verifactu.chainExport.success': 'Chain downloaded',
  'invoice.verifactu.chainExport.error': 'The chain could not be exported.',
  'invoice.verifactu.chainExport.from': 'From',
  'invoice.verifactu.chainExport.to': 'To',
  'invoice.verifactu.chainExport.rangeInvalid': 'The start date is after the end date.',

  // Abgleich mit der AEAT (ConsultaFactuSistemaFacturacion) — Plan API 09-03/04
  'invoice.verifactu.abgleich.title': 'Reconciliation with the AEAT',
  'invoice.verifactu.abgleich.hint':
    'Asks the AEAT which registros it holds for the selected month and compares them with this chain. The case that matters is the other direction: an invoice we sent that is missing over there.',
  'invoice.verifactu.abgleich.action': 'Start reconciliation',
  'invoice.verifactu.abgleich.running': 'Reconciling …',
  'invoice.verifactu.abgleich.period.year': 'Year',
  'invoice.verifactu.abgleich.period.month': 'Month',
  'invoice.verifactu.abgleich.nifMissing':
    'The query needs the NIF and the company name of the organisation. Both live in the organisation profile (tax number and company name) — without them the AEAT rejects the query.',
  'invoice.verifactu.abgleich.requestFailed': 'The query could not be carried out.',
  'invoice.verifactu.abgleich.idle':
    'Not reconciled yet. The query costs a real round trip to the tax authority, so it only runs when you ask for it.',
  'invoice.verifactu.abgleich.notWired':
    'This installation has no connection to the AEAT configured. Reconciliation becomes available once the operator has set it up.',
  'invoice.verifactu.abgleich.blocker':
    'The query was not sent because a prerequisite is missing. That is fixable — and not an outage.',
  'invoice.verifactu.abgleich.error': 'The AEAT rejected the query.',
  'invoice.verifactu.abgleich.incomplete':
    'Careful: the AEAT announced a further page but supplied no key for it. The list below is incomplete — the missing invoices have NOT been checked.',
  'invoice.verifactu.abgleich.emptyPeriod':
    'Neither the AEAT nor this chain holds any registro for the selected month.',
  'invoice.verifactu.abgleich.allMatching': 'Every compared invoice matches.',
  'invoice.verifactu.abgleich.matching': 'Matching',
  'invoice.verifactu.abgleich.pages': 'Pages queried',
  'invoice.verifactu.abgleich.art.fehltBeiDerAeat': 'Missing at the AEAT',
  'invoice.verifactu.abgleich.art.zustandWeichtAb': 'State differs',
  'invoice.verifactu.abgleich.art.fehltBeiUns': 'Missing in this chain',
  'invoice.verifactu.abgleich.art.deckungsgleich': 'Matching',
  'invoice.verifactu.abgleich.column.invoiceNumber': 'Invoice number',
  'invoice.verifactu.abgleich.column.sequence': 'Position in the chain',
  'invoice.verifactu.abgleich.column.estado': 'State at the AEAT',
  'invoice.verifactu.abgleich.column.error': 'AEAT error',
  'invoice.verifactu.chainHealth.title': 'Invoice chain health',
  'invoice.verifactu.chainHealth.hint':
    'Result of the last nightly check. The chain links every invoice to its predecessor — once it breaks, immutability under RD 1007/2023 can no longer be evidenced.',
  'invoice.verifactu.chainHealth.neverRun':
    'No check has run yet. That does not mean the chain is sound — it means it has not been checked.',
  'invoice.verifactu.chainHealth.scope.invoice': 'Invoice chain',
  'invoice.verifactu.chainHealth.scope.event': 'Event chain (Registro de Eventos)',
  'invoice.verifactu.chainHealth.records': 'Entries checked',
  'invoice.verifactu.chainHealth.lastRun': 'Last check',
  'invoice.verifactu.chainHealth.intact': 'Chain intact',
  'invoice.verifactu.chainHealth.broken': 'Chain broken',
  'invoice.verifactu.chainHealth.truncated':
    'More violations exist — the list is truncated. The first entry above is where it broke.',
  'invoice.verifactu.chainHealth.column.sequence': 'No.',
  'invoice.verifactu.chainHealth.column.kind': 'Kind of break',
  'invoice.verifactu.chainHealth.column.expected': 'Expected',
  'invoice.verifactu.chainHealth.column.actual': 'Found',
  'invoice.verifactu.chainHealth.kind.previous_hash_mismatch':
    'Link to the predecessor does not match',
  'invoice.verifactu.chainHealth.kind.sequence_gap': 'Gap in the sequence',
  'invoice.verifactu.chainHealth.kind.genesis_mismatch': 'Chain start does not match',
  'invoice.verifactu.chainHealth.kind.seed_mismatch': 'Carried-over chain start does not match',

  // Notice shown while VeriFactu is switched off for the organisation.
  'invoice.verifactu.inactive.title': 'VeriFactu is switched off for this company',
  'invoice.verifactu.inactive.description':
    'Invoices you finalize now are not reported to the AEAT — no registro is created and the PDF carries no QR code. Reporting becomes mandatory on 2027-01-01 (companies liable to corporate income tax) and on 2027-07-01 (everyone else).',
  'invoice.verifactu.inactive.link': 'Switch VeriFactu on now',
  'verifactu.nav.title': 'VeriFactu',
}
